/**
 * **« Crise — 14 jours d'arrêt forcé »** — SPEC-UI §7.5, la ligne `crise` du tableau des zones
 * de santé mentale : « écran dédié (séquence gérée serveur) ». PLAN §4.10.
 *
 * Le moteur gère la crise en un bloc (`resoudreCrise`, SPEC-MOTEUR §11.1) : l'horloge avance de
 * 14 jours, les abonnés fondent (la rente des anciennes vidéos versée pendant le blocage est déduite
 * de la perte — l'assurance burn-out), la SM remonte à 40, l'énergie à 100, le momentum retombe à
 * 0,8. Jusqu'ici tout cela arrivait **sans écran** : une ligne de journal (« Crise : 14 jours
 * d'arrêt forcé, N abonnés perdus ») et un toast de quatre secondes, pour le moment le plus dur
 * d'une run. Cet écran le RACONTE, plein cadre, comme `ChuteAuJalon` raconte la chute.
 *
 * ## Ce qu'il ne fait pas, et c'est le contrat
 *
 * - **Il ne calcule rien.** Les nombres viennent de l'événement `crisis_started` du moteur
 *   (`abonnes_perdus`, `jours`, `rente_deduite`) et de l'état d'après-crise (SM, énergie,
 *   momentum tels que le moteur les a posés). Aucune constante n'est recopiée.
 * - **Il ne décide rien.** Le dernier avertissement (§11.2 : une deuxième crise dans les 90
 *   jours sans remède est terminale) est un `warning` du moteur — on le dit parce qu'il est là,
 *   jamais parce qu'on l'a re-dérivé de l'historique.
 * - **Il reste muet** ⟦A-76 §3⟧ : l'ambiance de la zone rouge ne joue pas en crise, et
 *   `gameover_sting` n'existe pas (⏸ Mathieu). C'est l'écran qui parle.
 *
 * Une crise **terminale** (hardcore : game over ; normal : retour au dernier palier) passe aussi par ici,
 * d'abord — puis la chute ou l'écran de fin prennent la suite quand on continue.
 *
 * ⟦C48 · A-168 §3⟧ **Dans la DA** : ce n'est plus une carte de papier froissé posée sur l'aplat
 * jaune, c'est une `Fenetre` sur le fond de la chambre (`EcranFenetre.tsx`) — le titre et le
 * badge (« tu n'es pas éliminé ») dans le bandeau, le bouton dans le pied fixe. Les nombres, les
 * textes et la logique n'ont pas bougé d'un mot.
 *
 * ⟦C57 · lot 7 · A-174⟧ **Moins de texte** (Matt, 02/10 : une crise suit la règle normale, la
 * narration ne reste à l'écran que dans les FINS). L'écran garde le titre, le bilan en chiffres
 * (jours d'arrêt, abonnés perdus, santé mentale, énergie, momentum) et UNE phrase de 12 mots au
 * plus. Tout le reste s'ouvre par « Les dessous » : le récit de la crise, l'assurance burn-out (la
 * rente qui a retenu une part de la perte) et ce qui attend après. Le même assemblage
 * (`blocsDeCrise`, `game/fins.ts`) écrit le détail de l'entrée « Crise » du Journal : on le relit là
 * plus tard. Les textes vivent dans `game/fins.ts`.
 *
 * ⟦C60 · A-177⟧ **Le grand ménage : la crise ne parle que de santé mentale et d'énergie.** C'est déjà
 * tout ce que cet écran dit (jours d'arrêt, abonnés perdus, santé mentale, énergie, momentum) : la
 * crise n'a jamais eu de cause de loyer, de strike ou de réputation. Ne change que le mot du détail
 * « Les dessous » : l'assurance burn-out parle de « la rente de tes anciennes vidéos », plus de la
 * rente evergreen (`ligneAssuranceBurnout`, `game/fins.ts`). La fin de la crise terminale mène à
 * l'écran de fin du burn-out, ou à la chute au dernier palier en mode normal.
 */

import type { GameState } from "@maxime/shared";

import { DessousDuJournal } from "../../components/DessousDuJournal";
import { EcranFenetre } from "../../components/EcranFenetre";
import { PHRASE_DE_CRISE, blocsDeCrise, varianteDeCrise } from "../../game/fins";
import { nombre } from "../../game/format";
import { detailJournal } from "../../game/journal";
import type { CriseEnAttente } from "../../stores/runStore";

export function Crise({
  crise,
  state,
  nomDeChaine,
  onContinuer,
}: {
  crise: CriseEnAttente;
  /** L'état d'APRÈS la crise — c'est lui qui dit où en sont la SM, l'énergie, le momentum. */
  state: GameState;
  nomDeChaine: string;
  onContinuer(): void;
}) {
  const variante = varianteDeCrise(crise);
  // ⟦C57 · lot 7⟧ Ce que l'écran ne dit plus : le récit, l'assurance burn-out, la suite. Le même
  // assemblage (`blocsDeCrise`) écrit l'entrée du Journal.
  const detail = detailJournal(
    "La crise, en détail",
    blocsDeCrise({ jours: crise.jours, rente_deduite: crise.rente_deduite, variante, nomDeChaine }),
  );
  return (
    <EcranFenetre
      titre={`Crise : ${nombre(crise.jours)} jours d'arrêt forcé`}
      icone="🧠"
      badge={crise.terminal ? "c'était la deuxième" : "tu n'es pas éliminé"}
      className="fenetre-crise"
      pied={
        <button className="bouton primaire grand" type="button" onClick={onContinuer}>
          {crise.terminal ? "Voir ce qu'il reste" : "Reprendre, doucement"}
        </button>
      }
    >
      <div className="chute-bilan">
        <div className="chute-ligne">
          <span className="chute-avant">{nombre(crise.jours)} jours sans rien publier</span>
          <span className="chute-fleche" aria-hidden="true">
            ↓
          </span>
          <span className="chute-apres">−{nombre(crise.abonnes_perdus)} abonnés</span>
        </div>
        <div className="chute-ligne monte">
          <span className="chute-avant">santé mentale à zéro</span>
          <span className="chute-fleche" aria-hidden="true">
            ↑
          </span>
          <span className="chute-apres">
            ❤ {Math.round(state.mental)} · ⚡ {Math.round(state.energy)} · momentum ×
            {state.momentum.toFixed(2).replace(".", ",")}
          </span>
        </div>
      </div>

      <p className="chute-reprise">{PHRASE_DE_CRISE[variante]}</p>

      {detail ? (
        <div className="dessous-pose">
          <DessousDuJournal detail={detail} variante="texte" />
        </div>
      ) : null}
    </EcranFenetre>
  );
}
